import { env } from '@/config/env';
import { ApiError } from '@/services/ApiError';

/**
 * Cloudflare Turnstile, run on demand at the moment a form is submitted.
 *
 * WHY ON DEMAND, NOT A WIDGET ON THE FORM
 *   The check runs as the visitor presses Send and shows only when Cloudflare
 *   needs a tick. Forms keep their layout, and a visitor who never submits never
 *   sees it. Each token is single-use, so every submission asks for a new one.
 *
 * FAILS CLOSED
 *   Without a site key, or if the script cannot load, this throws and the form
 *   does not submit. A form that quietly skipped the check would reopen the
 *   gap the server-side check exists to close.
 *
 * THE ACTION
 *   The token is issued for one `action`, and the Edge Function refuses a token
 *   issued for a different one. Callers pass the same constants the server uses
 *   (see services/publicSubmission.ts).
 */

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** Long enough for a visitor to solve a tick-box challenge, short enough not to look stuck. */
const TIMEOUT_MS = 60_000;

const UNAVAILABLE =
  'We could not complete the security check. Please try again, or contact us on WhatsApp.';
const FAILED = 'The security check did not complete. Please try again.';

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  execute: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

interface TurnstileRenderOptions {
  sitekey: string;
  action: string;
  execution: 'execute';
  appearance: 'interaction-only';
  callback: (token: string) => void;
  'error-callback': () => void;
  'timeout-callback': () => void;
  'expired-callback': () => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptLoad: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);

  scriptLoad ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile loaded without its API'));
    };
    script.onerror = () => {
      // Forget the failed attempt so a later submission can try again.
      scriptLoad = null;
      script.remove();
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.appendChild(script);
  });

  return scriptLoad;
}

/**
 * A fresh single-use token for `action`. Resolves only when Cloudflare has
 * issued one; every other outcome throws an ApiError the form can show.
 */
export async function getCaptchaToken(action: string): Promise<string> {
  if (!env.turnstileSiteKey) throw new ApiError('unavailable', UNAVAILABLE);

  let api: TurnstileApi;
  try {
    api = await loadTurnstile();
  } catch {
    throw new ApiError('unavailable', UNAVAILABLE);
  }

  // A detached host: Turnstile needs an element to draw into, but nothing on the
  // page should own it. Removed as soon as the token is in hand.
  const host = document.createElement('div');
  // Pinned to the bottom of the viewport. An interactive challenge is drawn
  // inside this element, so it must be where the visitor is looking when they
  // press Send, not at the end of the page.
  host.setAttribute('role', 'group');
  host.setAttribute('aria-label', 'Security check');
  host.style.cssText =
    'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:1400;';
  document.body.appendChild(host);

  let widgetId: string | undefined;
  try {
    return await new Promise<string>((resolve, reject) => {
      const timer = window.setTimeout(
        () => reject(new ApiError('unavailable', FAILED)),
        TIMEOUT_MS,
      );
      const settle = (done: () => void) => {
        window.clearTimeout(timer);
        done();
      };

      widgetId = api.render(host, {
        sitekey: env.turnstileSiteKey,
        action,
        execution: 'execute',
        appearance: 'interaction-only',
        callback: (token) => settle(() => resolve(token)),
        'error-callback': () => settle(() => reject(new ApiError('unavailable', FAILED))),
        'timeout-callback': () => settle(() => reject(new ApiError('unavailable', FAILED))),
        'expired-callback': () => settle(() => reject(new ApiError('unavailable', FAILED))),
      });
      api.execute(widgetId);
    });
  } finally {
    if (widgetId !== undefined) api.remove(widgetId);
    host.remove();
  }
}
